const { jsPDF } = require("jspdf");

const buildContractPdf = ({
  blocks,
  signatures,
  logoDataUrl,
  logoRatio,
  font = "serif",
  boldValues = true,
  fonts,
  signatureFonts = {}
}) => {
  const pdf = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait"
  });
  const family = font === "modern" ? "PlusJakartaSans" : "times";
  if (font === "modern") {
    if (
      !fonts ||
      ["normal", "bold", "italic", "bolditalic"].some(style => !fonts[style])
    )
      throw new Error("Modern PDF fonts are unavailable");
    Object.entries(fonts).forEach(([style, data]) => {
      const filename = `PlusJakartaSans-${style}.ttf`;
      pdf.addFileToVFS(filename, data);
      pdf.addFont(filename, `${family}-${style}`, style);
    });
  }
  const setTextFont = style =>
    pdf.setFont(font === "modern" ? `${family}-${style}` : family, style);
  Object.values(signatureFonts).forEach(({ family, data }) => {
    const filename = `${family}.ttf`;
    pdf.addFileToVFS(filename, data);
    pdf.addFont(filename, family, "normal");
  });
  const setSignatureFont = signature => {
    const name = (signature.font || "Pacifico").trim();
    const embedded = signatureFonts[name];
    if (embedded) pdf.setFont(embedded.family, "normal");
    else if (signature.font)
      throw new Error(`The saved signature font "${name}" is unavailable.`);
    else pdf.setFont("times", "italic");
  };
  const left = 15;
  const right = 195;
  const contentWidth = right - left;
  const bottom = 272;
  let y = 17;

  pdf.addImage(logoDataUrl, "PNG", left, y, 30, 30 * logoRatio);
  y += 13;
  pdf.setDrawColor(220, 224, 229);
  pdf.line(left, y, right, y);
  y += 8;

  const addPage = () => {
    pdf.addPage();
    y = 18;
  };
  const ensureSpace = height => {
    if (y + height > bottom) addPage();
  };
  const richLines = (runs, width, baseStyle) => {
    const lines = [];
    let line = [];
    let used = 0;
    let pendingSpace = false;
    const finish = () => {
      if (line.length) lines.push(line);
      line = [];
      used = 0;
      pendingSpace = false;
    };
    const append = (text, style) => {
      setTextFont(style);
      const width = pdf.getTextWidth(text);
      const previous = line[line.length - 1];
      if (previous?.style === style) previous.text += text;
      else line.push({ text, style });
      used += width;
    };
    runs.forEach(run => {
      const bold = baseStyle === "bold" || (boldValues && run.bold);
      const style = bold
        ? run.italic
          ? "bolditalic"
          : "bold"
        : run.italic
        ? "italic"
        : "normal";
      String(run.text || "")
        .split(/(\s+)/)
        .filter(Boolean)
        .forEach(token => {
          if (/^\s+$/.test(token)) {
            pendingSpace = line.length > 0;
            return;
          }
          setTextFont(style);
          const space = pendingSpace ? pdf.getTextWidth(" ") : 0;
          if (used + space + pdf.getTextWidth(token) > width && line.length)
            finish();
          if (pendingSpace) append(" ", style);
          pendingSpace = false;
          if (pdf.getTextWidth(token) <= width) append(token, style);
          else {
            for (const character of Array.from(token)) {
              if (used + pdf.getTextWidth(character) > width && line.length)
                finish();
              append(character, style);
            }
          }
        });
    });
    finish();
    return lines;
  };
  const drawBlock = (block, options = {}) => {
    const normalized = `${block.text || ""}`.replace(/\s+/g, " ").trim();
    if (!normalized) return;
    const fontSize = options.fontSize || 10;
    const lineHeight = fontSize * 0.352778 * (options.leading || 1.45);
    const spacingBefore = options.spacingBefore || 0;
    const spacingAfter = options.spacingAfter || 2.6;
    const blockWidth = contentWidth - (options.indent || 0);
    setTextFont(options.style || "normal");
    pdf.setFontSize(fontSize);
    const lines = block.runs
      ? richLines(block.runs, blockWidth, options.style)
      : pdf.splitTextToSize(normalized, blockWidth);
    if (options.keepTogether)
      ensureSpace(
        spacingBefore +
          lines.length * lineHeight +
          spacingAfter +
          (options.keepWithNext ? 13 : 0)
      );
    y += spacingBefore;
    lines.forEach(line => {
      ensureSpace(lineHeight + spacingAfter);
      if (Array.isArray(line)) {
        let x = left + (options.indent || 0);
        line.forEach(run => {
          setTextFont(run.style);
          pdf.text(run.text, x, y);
          x += pdf.getTextWidth(run.text);
        });
      } else pdf.text(line, left + (options.indent || 0), y);
      y += lineHeight;
    });
    y += spacingAfter;
  };

  blocks.forEach(block => {
    if (block.tag === "h1") {
      drawBlock(block, {
        fontSize: 16,
        style: "bold",
        leading: 1.2,
        spacingAfter: 4,
        keepTogether: true,
        keepWithNext: true
      });
    } else if (block.tag === "h2") {
      drawBlock(block, {
        fontSize: 12.5,
        style: "bold",
        leading: 1.25,
        spacingBefore: 3,
        spacingAfter: 3,
        keepTogether: true,
        keepWithNext: true
      });
    } else if (/^h[3-6]$/.test(block.tag)) {
      drawBlock(block, {
        fontSize: 11,
        style: "bold",
        spacingBefore: 2,
        keepTogether: true,
        keepWithNext: true
      });
    } else if (block.tag === "li") {
      drawBlock(block, { indent: 6, spacingAfter: 1.8 });
    } else {
      drawBlock(block);
    }
  });

  if (signatures.length) {
    const signatureWidth = 80;
    for (let offset = 0; offset < signatures.length; offset += 2) {
      pdf.setFontSize(14);
      const row = signatures.slice(offset, offset + 2).map(signature => {
        setSignatureFont(signature);
        return {
          ...signature,
          lines: pdf.splitTextToSize(signature.name || "", signatureWidth)
        };
      });
      const nameHeight =
        Math.max(...row.map(signature => signature.lines.length), 1) * 6;
      ensureSpace(30 + nameHeight);
      y += 4;
      pdf.setDrawColor(190, 196, 203);
      pdf.line(left, y, right, y);
      y += 10;
      row.forEach((signature, index) => {
        const x = left + index * 95;
        setSignatureFont(signature);
        pdf.setFontSize(14);
        signature.lines.forEach((line, index) =>
          pdf.text(line, x, y + index * 6)
        );
        const baseline = y + nameHeight;
        pdf.setDrawColor(60, 65, 70);
        pdf.line(x, baseline, x + signatureWidth, baseline);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7.5);
        pdf.text(signature.party, x, baseline + 5);
        if (signature.id) {
          pdf.setFont("courier", "normal");
          pdf.setFontSize(5.5);
          pdf.text(
            pdf.splitTextToSize(`ID: ${signature.id}`, signatureWidth),
            x,
            baseline + 9
          );
        }
      });
      y += nameHeight + 15;
    }
  }

  const total = pdf.internal.getNumberOfPages();
  const footerWidth = 17.6;
  const footerHeight = Math.min(7, footerWidth * logoRatio);
  for (let page = 1; page <= total; page += 1) {
    pdf.setPage(page);
    pdf.addImage(
      logoDataUrl,
      "PNG",
      left,
      287 - footerHeight,
      footerWidth,
      footerHeight
    );
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.5);
    pdf.setTextColor(110, 105, 108);
    pdf.setDrawColor(230, 224, 227);
    pdf.setLineWidth(0.15);
    pdf.line(left, 278, right, 278);
    pdf.text(`${page} / ${total}`, right, 287, { align: "right" });
  }

  return pdf;
};

module.exports = { buildContractPdf };
